// components/GlobalActionList.tsx

import React, { useEffect, useState } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { isYesterday } from 'date-fns';
import { GlobalAction } from '../types/GlobalAction';
import { Spinner } from 'react-bootstrap';
import DeleteConfirmation from 'common/components/ui/DeleteConfirmation';
import '../styles/GlobalActionList.css';

const SDG_DEFAULT = 13;

interface GlobalActionListProps {
  sdg?: 'all' | number;
  list?: GlobalAction[];
  onEditClick?: (globalAction: GlobalAction) => void;
  onDeleteItem: (index: number) => void;
  isLoading: boolean;
}

const formatFriendlyDate = (date?: Date): string => {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (isYesterday(d)) {
    return `Yesterday @ ${format(d, 'p')}`;
  }
  const diff = Date.now() - d.getTime();
  if (diff < 1000 * 60 * 60 * 24) {
    return formatDistanceToNow(d, { addSuffix: true });
  }
  return format(d, 'PPP @ p');
};

const GlobalActionList: React.FC<GlobalActionListProps> = ({
  sdg,
  list,
  onEditClick,
  onDeleteItem,
  isLoading,
}) => {
  const [actions, setActions] = useState<GlobalAction[]>([]);
  const [loading, setLoading] = useState(isLoading);
  const selectedSdg = sdg || SDG_DEFAULT;

  useEffect(() => {
    setActions(list || []);
    setLoading(false);
  }, [list]);

  return (
    <div className="container mt-4">
      <h3>{actions.length} Actions for SDG {selectedSdg}</h3>
      {loading ? (
        <Spinner animation="border" />
      ) : (
        <div>
          {actions.map((action, index) => (
            <div key={action.id} className="action-item row">
              <div
                className="clickable-content col-md-9"
                onClick={onEditClick ? () => onEditClick(action) : undefined}
              >
                <h5>{action.title}</h5>
                <p>{action.description}</p>
                {action.sdg && <p><strong>SDG:</strong> {action.sdg}</p>}
                <div className="meta-info">
                  <small><strong>Created:</strong> {formatFriendlyDate(action.createdAt)}</small><br />
                  <small><strong>Last Updated:</strong> {formatFriendlyDate(action.updatedAt)}</small>
                </div>
              </div>

              <div className="action-controls col-md-3">
                <DeleteConfirmation
                  index={index}
                  item={{ name: action.title }}
                  label={'Remove'}
                  buttonIsCustom={true}
                  onDelete={() => onDeleteItem(index)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GlobalActionList;
